import type { ProcessDetail, ProcessSummary } from '@bk/contracts';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { navigate } from '../../lib/router';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Badge, Button, Card, Empty, ErrorText, PageTitle, Select } from '../../ui';
import { OpenConversations } from './Builder';
import { ContractEditor } from './ContractEditor';

export function Processes() {
  const { tenant, isManager } = useTenant();
  const processes = useTenantQuery<{ data: ProcessSummary[] }>(['processes'], '/processes');
  return (
    <>
      <PageTitle
        title="Processi"
        subtitle="Come lavora l'azienda: per ogni processo un contratto stabile e una procedura ratificata."
        actions={
          isManager && (
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => navigate(`/t/${tenant.id}/processi/nuovo`)} title="Formato interno, per uso tecnico">
                Editor avanzato
              </Button>
              <Button onClick={() => navigate(`/t/${tenant.id}/processi/costruisci`)}>Descrivi un processo</Button>
            </div>
          )
        }
      />
      {isManager && <OpenConversations />}
      <ErrorText>{processes.error ? errorMessage(processes.error) : ''}</ErrorText>
      {processes.data?.data.length === 0 && <Empty>Nessun processo. Usa “Descrivi un processo” e racconta come lavorate.</Empty>}
      {!!processes.data?.data.length && (
        <Card className="py-2">
          <div className="divide-y divide-black/5">
            {processes.data.data.map((p) => (
              <button key={p.id} className="flex w-full flex-wrap items-center gap-3 py-3 text-left hover:bg-black/[0.02]" onClick={() => navigate(`/t/${tenant.id}/processi/${p.id}`)}>
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{p.name}</p>
                  <p className="text-xs text-ink/55">
                    contratto v{p.contract_version} · {p.procedure ? `procedura v${p.procedure.version}` : 'nessuna procedura attiva'} · {p.open_runs} esecuzioni aperte
                  </p>
                </div>
                {p.pending_procedure_id && <Badge tone="amber">procedura da ratificare</Badge>}
                {p.status === 'sospeso' && <Badge>sospeso</Badge>}
              </button>
            ))}
          </div>
        </Card>
      )}
    </>
  );
}

/** Nuovo processo: si parte da un esempio o da zero; si salva solo un contratto valido. */
export function NewProcess() {
  const { tenant, t } = useTenant();
  const [source, setSource] = useState('');
  const [valid, setValid] = useState(false);
  const examples = useQuery({ queryKey: ['process-examples'], queryFn: () => api<{ data: Array<{ key: string; name: string; source: string }> }>('/process-examples') });
  const create = useMutation({ mutationFn: () => api<ProcessDetail>(t('/processes'), { body: { source } }), onSuccess: (p) => navigate(`/t/${tenant.id}/processi/${p.id}`) });
  return (
    <>
      <PageTitle
        title="Editor avanzato"
        subtitle="Formato interno del contratto, per uso tecnico. Di norma lo scrive il sistema dalla conversazione."
        actions={
          <div className="flex items-center gap-2">
            <div className="w-56">
              <Select value="" onChange={(e) => setSource(examples.data?.data.find((x) => x.key === e.target.value)?.source ?? source)}>
                <option value="">Parti da un esempio…</option>
                {examples.data?.data.map((x) => (
                  <option key={x.key} value={x.key}>
                    {x.name}
                  </option>
                ))}
              </Select>
            </div>
            <Button disabled={!valid || create.isPending} onClick={() => create.mutate()}>
              Crea processo
            </Button>
          </div>
        }
      />
      <ErrorText>{create.error ? errorMessage(create.error) : ''}</ErrorText>
      <ContractEditor source={source} onChange={setSource} onValid={setValid} />
    </>
  );
}
